import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Select,
  AutoComplete,
  Radio,
  InputNumber,
  Button,
  Table,
  Space,
  Collapse,
  Row,
  Col,
  message,
  Spin,
  DatePicker,
  Tooltip,
  Checkbox,
} from "antd";
import moment from "moment";
import dayjs from "dayjs";
import http from "../../../helpers/http";
import config from "../../../config/config";
import { DeleteOutlined } from "@ant-design/icons";

const { Panel } = Collapse;

// businessHoursOptions.js
const openingHoursOptions = [
  { label: "6:00 am", value: "6:00 am" },
  { label: "7:00 am", value: "7:00 am" },
  { label: "8:00 am", value: "8:00 am" },
  { label: "9:00 am", value: "9:00 am" },
  { label: "10:00 am", value: "10:00 am" },
  { label: "11:00 am", value: "11:00 am" },
  { label: "12:00 pm", value: "12:00 pm" },
];

const closingHoursOptions = [
  { label: "4:00 pm", value: "4:00 pm" },
  { label: "5:00 pm", value: "5:00 pm" },
  { label: "6:00 pm", value: "6:00 pm" },
  { label: "7:00 pm", value: "7:00 pm" },
  { label: "8:00 pm", value: "8:00 pm" },
  { label: "9:00 pm", value: "9:00 pm" },
  { label: "10:00 pm", value: "10:00 pm" },
];

const DEFAULT_BUSINESS_HOURS = [
  { day: "Monday", open: null, close: null, isClosed: false },
  { day: "Tuesday", open: null, close: null, isClosed: false },
  { day: "Wednesday", open: null, close: null, isClosed: false },
  { day: "Thursday", open: null, close: null, isClosed: false },
  { day: "Friday", open: null, close: null, isClosed: false },
  { day: "Saturday", open: null, close: null, isClosed: false },
  { day: "Sunday", open: null, close: null, isClosed: false },
];

const FormDetailsForm = ({
  form: antdForm,
  initialData,
  admin,
  mode = "edit",
  userId,
  onSubmit,
}) => {
  const [form] = Form.useForm(antdForm);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [featuredServiceOptions, setFeaturedServiceOptions] = useState([]);
  const [typologyOptions, setTypologyOptions] = useState([]);
  const [designStyleOptions, setDesignStyleOptions] = useState([]);
  const [teamRangeOptions, setTeamRangeOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [productPositionings, setProductPositionings] = useState([]);
  const [projectSizeOptions, setProjectSizeOptions] = useState([]);
  const [averageBudgetOptions, setAverageBudgetOptions] = useState([]);
  const [minimumProjectFeeOptions, setMinimumProjectFeeOptions] = useState([]);
  const [roleConstraints, setRoleConstraints] = useState([]);
  const [selectedMinimumFeeCurrency, setSelectedMinimumFeeCurrency] =
    useState("INR");
  const [selectedUnit, setSelectedUnit] = useState("sq.ft.");
  const [selectedCurrency, setSelectedCurrency] = useState("INR");
  const [countryCodes, setCountryCodes] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [editRequestStatus, setEditRequestStatus] = useState(null);

  // New taxonomy state
  const [taxonomyTypes, setTaxonomyTypes] = useState([]);
  const [taxonomySubtypes, setTaxonomySubtypes] = useState([]);
  const [selectedBusinessTypes, setSelectedBusinessTypes] = useState([]);
  const [subtypesByType, setSubtypesByType] = useState({});

  const [date, setDate] = useState(null);
  const currentYear = new Date().getFullYear();
  const minYear = currentYear - 100;
  const minDate = new Date(minYear, 0, 1);
  const inputStyle = { width: 500 };

  const [loading, setLoading] = useState(false);
  const base_url = config.api_url;
  const staticFees = {
    INR: [
      "Under 1,00,000 INR",
      "1,00,000 - 5,00,000 INR",
      "5,00,000 - 10,00,000 INR",
      "Above 10,00,000 INR",
    ],
    USD: [
      "Under 1200 USD",
      "1200-6000 USD",
      "6,000-12,000 USD",
      "Above 12,000 USD",
    ],
  };
  // Fetch options for select fields
  const fetchOptions = async () => {
    setLoading(true);
    try {
      const { data } = await http.get(`${base_url}business/options`);
      const servicesData = await http.get(`${base_url}services`);
      const {
        project_scope_preferences,
        design_styles,
        project_typologies,
        services,
        team_member_ranges,
        project_locations,
        product_positionings,
        project_sizes,
        average_budget,
      } = data;
      // setServiceOptions(services.map((item) => ({ label: item, value: item })));
      setServiceOptions(servicesData?.data || []);
      setTypologyOptions(
        project_typologies.map((item) => ({ label: item, value: item }))
      );
      setDesignStyleOptions(
        design_styles.map((item) => ({ label: item, value: item }))
      );
      setTeamRangeOptions(
        team_member_ranges.map((item) => ({ label: item, value: item }))
      );
      setLocationOptions(
        project_locations.map((item) => ({ label: item, value: item }))
      );
      setProductPositionings(
        product_positionings.map((item) => ({ label: item, value: item }))
      );
      setProjectSizeOptions(
        project_sizes.map((item) => ({
          label: item.size,
          value: item.size,
          unit: item.unit,
        }))
      );
      setAverageBudgetOptions(
        average_budget.map((item) => ({
          label: item.budget,
          value: item.budget,
          currency: item.currency,
        }))
      );
    } catch (error) {
      // message.error("Failed to fetch options");
    } finally {
      setLoading(false);
    }
  };

  const handleMinimumFeeCurrencyChange = (value) => {
    setSelectedMinimumFeeCurrency(value);
    setMinimumProjectFeeOptions(
      staticFees[value].map((fee) => ({ label: fee, value: fee }))
    );

    form.setFieldsValue({
      project_mimimal_fee: {
        fee: undefined,
        currency: value,
      },
    });
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);

    if (newDate) {
      const yearString = newDate.year().toString();
      form.setFieldsValue({
        establishment_year: yearString,
      });
    } else {
      setTimeout(() => {
        form.setFieldsValue({
          establishment_year: undefined,
        });
      }, 0);
    }
  };

  const handleUnitChange = (value) => {
    setSelectedUnit(value);
    let selectedSizes = [];
    if (mode === "edit" && value === initialData.project_sizes?.unit) {
      selectedSizes = initialData.project_sizes?.sizes || [];
    }
    const currentSizes = form.getFieldValue(["project_sizes", "sizes"]) || [];
    const validSizes =
      selectedSizes.length > 0
        ? selectedSizes.filter((size) =>
            projectSizeOptions.some(
              (option) => option.value === size && option.unit === value
            )
          )
        : currentSizes.filter((size) =>
            projectSizeOptions.some(
              (option) => option.value === size && option.unit === value
            )
          );
    form.setFieldsValue({ project_sizes: { sizes: validSizes, unit: value } });
  };

  // Handle currency change for average budget
  const handleCurrencyChange = (value) => {
    setSelectedCurrency(value);
    let selectedBudgets = [];
    if (mode === "edit" && value === initialData.avg_project_budget?.currency) {
      selectedBudgets = initialData.avg_project_budget?.budgets || [];
    }
    const currentBudgets =
      form.getFieldValue(["avg_project_budget", "budgets"]) || [];

    const validBudgets =
      selectedBudgets.length > 0
        ? selectedBudgets.filter((budget) =>
            averageBudgetOptions.some(
              (option) => option.value === budget && option.currency === value
            )
          )
        : currentBudgets.filter((budget) =>
            averageBudgetOptions.some(
              (option) => option.value === budget && option.currency === value
            )
          );
    form.setFieldsValue({
      avg_project_budget: { budgets: validBudgets, currency: value },
    });
  };

  const fetchCountryCodes = async () => {
    const codesResponse = await http.get(`${base_url}general/countries/codes`);
    setCountryCodes(
      codesResponse.data.map((item, index) => ({
        key: `${item.phone_code}-${index}`,
        label: item.phone_code,
        value: item.phone_code,
      }))
    );
  };

  const fetchTaxonomy = async () => {
    try {
      const [typesRes, subtypesRes] = await Promise.all([
        http.get(`${base_url}admin/business-users/taxonomy/types`),
        http.get(`${base_url}admin/business-users/taxonomy/subtypes`),
      ]);
      if (typesRes?.data) setTaxonomyTypes(typesRes.data);
      if (subtypesRes?.data) setTaxonomySubtypes(subtypesRes.data);
    } catch (error) {
      console.error("Failed to fetch taxonomy:", error);
    }
  };

  useEffect(() => {
    if (initialData.establishment_year) {
      setDate(dayjs(initialData.establishment_year.data));
      form.setFieldsValue({
        establishment_years: dayjs(initialData.establishment_year.data),
      });
    }
  }, [initialData, form]);

  // Pre-populate business_type_new from existing data
  useEffect(() => {
    if (initialData?.business_type_new && Array.isArray(initialData.business_type_new)) {
      const types = [];
      const subtypesMap = {};
      for (const entry of initialData.business_type_new) {
        const typeId = entry.type?._id || entry.type;
        if (typeId) {
          types.push(String(typeId));
          subtypesMap[String(typeId)] = (entry.subtypes || []).map((s) =>
            String(s?._id || s)
          );
        }
      }
      setSelectedBusinessTypes(types);
      setSubtypesByType(subtypesMap);
    }
  }, [initialData]);

  const handleBusinessTypesChange = (newTypes) => {
    setSelectedBusinessTypes(newTypes);
    // Remove subtypes for deselected types
    setSubtypesByType((prev) => {
      const updated = {};
      for (const t of newTypes) {
        updated[t] = prev[t] || [];
      }
      return updated;
    });
  };

  const handleSubtypesChange = (typeId, newSubtypes) => {
    setSubtypesByType((prev) => ({
      ...prev,
      [typeId]: newSubtypes,
    }));
  };

  // Keep form's business_type_new field in sync with local state so it gets
  // submitted via the parent form's onFinish handler
  useEffect(() => {
    form.setFieldsValue({
      business_type_new: selectedBusinessTypes.map((typeId) => ({
        type: typeId,
        subtypes: subtypesByType[typeId] || [],
      })),
    });
  }, [selectedBusinessTypes, subtypesByType, form]);

  useEffect(() => {
    const currency = initialData.project_mimimal_fee?.currency || "INR";
    setMinimumProjectFeeOptions(
      staticFees[currency].map((fee) => ({ label: fee, value: fee }))
    );
    fetchOptions();
    fetchCountryCodes();
    fetchTaxonomy();

    if (admin?.role !== "Super Admin") {
      if (admin?.role?.permissions) {
        const lockedDataPermission = admin.role.permissions.find(
          (per) => per?.constraints?.lockedFields?.length
        );
        if (lockedDataPermission) {
          setRoleConstraints(lockedDataPermission?.constraints?.lockedFields);
        }
      }
    }

    const userEditRequest = initialData?.editRequests?.find(
      (it) => it.user === initialData?._id
    );

    if (userEditRequest) {
      setEditRequestStatus(userEditRequest.status);
    } else {
      setEditRequestStatus(null);
    }
  }, [initialData, admin]); // Added initialData to dependencies

  // Columns for owners table
  const ownerColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Phone",
      render: (_, record) => `${record.country_code}${record.phone}`,
      key: "phone",
    },
    {
      title: "WhatsApp",
      render: (_, record) =>
        `${record.whatsapp_country_code}${record.whatsapp_no}`,
      key: "whatsapp",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record, index) => (
        <Button
          type="link"
          onClick={() => {
            const owners = form.getFieldValue("owners");
            owners.splice(index, 1);
            form.setFieldsValue({ owners });
          }}
        >
          Delete
        </Button>
      ),
    },
  ];

  useEffect(() => {
    if (mode === "create") {
      form.resetFields();
      setInitialFormDataForCreate();
    } else if (mode === "edit" && initialData) {
      const business_hours =
        initialData.business_hours && initialData.business_hours.length > 0
          ? initialData.business_hours
          : DEFAULT_BUSINESS_HOURS;

      // Find first non-closed day for global open/close
      const firstOpenDay = business_hours.find(
        (d) => !d.isClosed && d.open && d.close
      );

      form.setFieldsValue({
        ...initialData,
        addresses: initialData.addresses || [],
        business_hours: business_hours,
        business_hours_global: {
          open: firstOpenDay?.open || "",
          close: firstOpenDay?.close || "",
        },
        establishment_years: initialData.establishment_year?.data
          ? dayjs(initialData.establishment_year.data)
          : undefined,
        project_scope: initialData.project_scope?.data,
        project_typology: initialData.project_typology?.data,
        design_style: initialData.design_style?.data,
        team_range: initialData.team_range?.data,
        project_location: initialData.project_location?.data,
        product_positionings: initialData.product_positionings || [],
        project_sizes: {
          sizes: initialData.project_sizes?.sizes,
          unit: initialData.project_sizes?.unit || "sq.ft.",
        },
        avg_project_budget: {
          budgets: initialData.avg_project_budget?.budgets || [],
          currency: initialData.avg_project_budget?.currency,
        },
        project_mimimal_fee: {
          fee: initialData.project_mimimal_fee?.fee,
          currency: initialData.project_mimimal_fee?.currency || "INR",
        },
        // renovation_work: initialData.renovation_work ? "Yes" : "No",
        renovation_work:
          initialData.renovation_work === true ||
          initialData.renovation_work === "true",
        website_link: initialData?.website_link || "",
      });
      setDate(
        initialData.establishment_year?.data
          ? dayjs(initialData.establishment_year.data)
          : null
      );
      setSelectedUnit(initialData.project_sizes?.unit || "sq.ft.");
      setSelectedCurrency(initialData.avg_project_budget?.currency);
      setSelectedMinimumFeeCurrency(
        initialData.project_mimimal_fee?.currency || "INR"
      );
    }
  }, [initialData, mode, form]);

  const setInitialFormDataForCreate = () => {
    form.setFieldsValue({
      project_mimimal_fee: { currency: "INR" },
      project_sizes: { unit: "sq.ft." },
      avg_project_budget: { currency: undefined },
      owners: [],
      featured_services: [],
      services: [],
      project_scope: [],
      project_typology: [],
      design_style: [],
      team_range: undefined,
      project_location: undefined,
      product_positionings: [],
      renovation_work: undefined,
      website_link: undefined,
      linkedin_link: undefined,
      instagram_handle: undefined,
      establishment_years: undefined,
      business_hours: [
        { day: "Monday", open: "", close: "", isClosed: false },
        { day: "Tuesday", open: "", close: "", isClosed: false },
        { day: "Wednesday", open: "", close: "", isClosed: false },
        { day: "Thursday", open: "", close: "", isClosed: false },
        { day: "Friday", open: "", close: "", isClosed: false },
        { day: "Saturday", open: "", close: "", isClosed: false },
        { day: "Sunday", open: "", close: "", isClosed: false },
      ],
    });
  };

  const handleFormSubmit = async (values) => {
    // Reformat complex objects before sending
    const formattedValues = {
      ...values,
      addresses:
        values.addresses?.filter((item) => item?.type && item?.address) || [],
      business_hours: values.business_hours || [],
      establishment_year: values.establishment_years
        ? { data: values.establishment_years.year().toString() }
        : undefined,
      project_scope: { data: values.project_scope || [] },
      project_typology: { data: values.project_typology || [] },
      design_style: { data: values.design_style || [] },
      team_range: { data: values.team_range },
      project_location: { data: values.project_location },
      product_positionings: values.product_positionings || [], // Changed to handle direct array
      project_sizes: {
        sizes: values.project_sizes?.sizes || [],
        unit: values.project_sizes?.unit || "sq.ft.",
      },
      avg_project_budget: {
        budgets: values.avg_project_budget?.budgets || [],
        currency: values.avg_project_budget?.currency,
      },
      project_mimimal_fee: {
        fee: values.project_mimimal_fee?.fee || undefined,
        currency: values.project_mimimal_fee?.currency || "INR",
      },
      renovation_work: values.renovation_work,
      business_type_new: selectedBusinessTypes.map((typeId) => ({
        type: typeId,
        subtypes: subtypesByType[typeId] || [],
      })),
    };
    if (onSubmit) {
      onSubmit(formattedValues);
    } else {
      setLoading(true);
      try {
        const response = await http.post(
          `${base_url}business/business-details/${initialData._id}`,
          formattedValues
        );
        if (response?.data) {
          message.success("Form details updated successfully");
        } else {
          message.error(response?.message || "Failed to update form details.");
        }
      } catch (error) {
        message.error(
          error?.message || "An error occurred while updating form details."
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleEditRequest = async () => {
    const response = await http.put(
      `${config.api_url}admin/content/options/edit-request`,
      {
        user: initialData?._id,
        roleUser: admin.id,
      }
    );
    if (response?.data) {
      message.success("Edit Request Sent Successfully");
      setEditRequestStatus("pending");
    }
  };

  return (
    <Spin spinning={loading}>
      <div>
        {roleConstraints?.includes("website_link") && (
          <Button
            type="primary"
            style={{
              marginBottom: "20px",
              backgroundColor:
                editRequestStatus === "approved" ? "green" : undefined,
              color: editRequestStatus === "pending" ? "grey" : "white",
            }}
            onClick={() => handleEditRequest()}
            disabled={
              editRequestStatus === "pending" ||
              editRequestStatus === "approved"
            }
          >
            {editRequestStatus === "pending"
              ? "Edit Requested for Locked Data"
              : editRequestStatus === "approved"
              ? "Locked Fields Edit Allowed"
              : "Request to Edit Locked Fields"}
          </Button>
        )}
        <Collapse defaultActiveKey={["1", "2", "3"]}>
          <Panel header="Owner Details" key="1">
            <Form.List name="owners">
              {(fields, { add, remove }) => (
                <>
                  {fields.length > 0 && (
                    <Table
                      rowKey={(record, index) => index}
                      columns={ownerColumns}
                      dataSource={fields
                        .map((field) => {
                          const owner =
                            form.getFieldValue("owners")[field.name];
                          return {
                            ...owner,
                            key: field.key,
                          };
                        })
                        .filter((owner) => owner.name || owner.email)} // Only show owners with at least a name or email
                      pagination={false}
                    />
                  )}
                  <Button
                    type="dashed"
                    onClick={() => add()}
                    style={{ width: "100%", marginTop: 16 }}
                  >
                    Add Owner
                  </Button>
                  {fields.map(({ key, name, ...restField }) => (
                    <div
                      key={key}
                      style={{
                        marginTop: 16,
                        padding: 16,
                        border: "1px solid #f0f0f0",
                      }}
                    >
                      <Form.Item
                        {...restField}
                        name={[name, "name"]}
                        label="Name"
                        rules={[]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "email"]}
                        label="Email"
                        rules={[]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item label="Phone">
                        <Row gutter={8}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "country_code"]}
                              noStyle
                              rules={[]}
                            >
                              <Select
                                showSearch
                                options={countryCodes}
                                placeholder="Code"
                                filterOption={(input, option) =>
                                  option.label
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Form.Item
                              {...restField}
                              name={[name, "phone"]}
                              noStyle
                              rules={[]}
                            >
                              <Input placeholder="Phone number" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form.Item>
                      <Form.Item label="WhatsApp Number">
                        <Row gutter={8}>
                          <Col span={8}>
                            <Form.Item
                              {...restField}
                              name={[name, "whatsapp_country_code"]}
                              noStyle
                              rules={[]}
                            >
                              <Select
                                showSearch
                                options={countryCodes}
                                placeholder="Code"
                                filterOption={(input, option) =>
                                  option.label
                                    .toLowerCase()
                                    .includes(input.toLowerCase())
                                }
                              />
                            </Form.Item>
                          </Col>
                          <Col span={16}>
                            <Form.Item
                              {...restField}
                              name={[name, "whatsapp_no"]}
                              noStyle
                              rules={[]}
                            >
                              <Input placeholder="WhatsApp number" />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Form.Item>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Panel>
          <Panel header="Service Details" key="2">
            {/* Hidden form item to register business_type_new field with the form */}
            <Form.Item name="business_type_new" hidden>
              <Input />
            </Form.Item>
            <Form.Item label="Business Types">
              <Select
                mode="multiple"
                placeholder="Select business types"
                value={selectedBusinessTypes}
                onChange={handleBusinessTypesChange}
                options={taxonomyTypes.map((t) => ({
                  label: t.type,
                  value: String(t._id),
                }))}
              />
            </Form.Item>

            {selectedBusinessTypes.map((typeId) => {
              const typeDoc = taxonomyTypes.find((t) => String(t._id) === typeId);
              const availableSubtypes = taxonomySubtypes.filter(
                (s) => String(s.type) === typeId
              );
              return (
                <Form.Item
                  key={typeId}
                  label={`Subtypes for ${typeDoc?.type || "Unknown"}`}
                >
                  <Select
                    mode="multiple"
                    placeholder={`Select subtypes for ${typeDoc?.type || ""}`}
                    value={subtypesByType[typeId] || []}
                    onChange={(val) => handleSubtypesChange(typeId, val)}
                    options={availableSubtypes.map((s) => ({
                      label: s.subtype,
                      value: String(s._id),
                    }))}
                  />
                </Form.Item>
              );
            })}

            {/* <Form.Item
            name="featured_services"
            label="Top 5 Products/Services"
            rules={[
              { required: true, message: "Featured services are required" },
            ]}
          >
            <Select
              mode="multiple"
              options={serviceOptions}
              placeholder="Select featured services"
              maxTagCount={5}
            />
          </Form.Item> */}
            <Form.Item
              name="services"
              label="Services"
              rules={[]}
              normalize={(value) =>
                Array.isArray(value)
                  ? [...new Set(value.map((v) => String(v).trim()).filter(Boolean))]
                  : value
              }
            >
              <Select
                mode="tags"
                options={serviceOptions}
                placeholder="Select, type, or paste comma-separated services"
                tokenSeparators={[',']}
              />
            </Form.Item>
            <Form.Item
              name="featured_services"
              label="Featured Services"
              rules={[]}
              normalize={(value) =>
                Array.isArray(value)
                  ? [...new Set(value.map((v) => String(v).trim()).filter(Boolean))]
                  : value
              }
            >
              <Select
                mode="tags"
                options={serviceOptions}
                placeholder="Select, type, or paste comma-separated services"
                tokenSeparators={[',']}
              />
            </Form.Item>
          </Panel>
          <Panel header="Project and Business Details" key="3">
            <Form.Item
              name="website_link"
              label="Website Link"
              rules={[]}
            >
              {/* <Tooltip
                title={
                  roleConstraints?.includes("instagram_handle") &&
                  editRequestStatus !== "approved"
                    ? "Website link is locked due to role restrictions"
                    : ""
                }
              >
              </Tooltip> */}
              <Input
                placeholder="Enter Website Link"
                style={inputStyle}
                disabled={
                  roleConstraints?.includes("website_link") &&
                  editRequestStatus !== "approved"
                }
              />
            </Form.Item>
            <Form.Item
              name="linkedin_link"
              label="LinkedIn Link"
              rules={[]}
            >
              {/* <Tooltip
                title={
                  roleConstraints?.includes("instagram_handle") &&
                  editRequestStatus !== "approved"
                    ? "Linkedin handle is locked due to role restrictions"
                    : ""
                }
              >
              </Tooltip> */}
              <Input
                placeholder="Enter Linkedin Profile URL"
                style={inputStyle}
                disabled={
                  roleConstraints?.includes("linkedin_link") &&
                  editRequestStatus !== "approved"
                }
              />
            </Form.Item>
            <Form.Item
              name="instagram_handle"
              label="Instagram Handle"
            >
              {/* <Tooltip
                title={
                  roleConstraints?.includes("instagram_handle") &&
                  editRequestStatus !== "approved"
                    ? "Instagram handle is locked due to role restrictions"
                    : ""
                }
              >
              </Tooltip> */}
              <Input
                placeholder="Enter Instagram handle (e.g. @username or username)"
                style={inputStyle}
                disabled={
                  roleConstraints?.includes("instagram_handle") &&
                  editRequestStatus !== "approved"
                }
              />
            </Form.Item>

            <Form.Item label="Business Hours">
              {/* Global Time Selection */}
              <Row gutter={12} style={{ marginBottom: 16 }}>
                <Col span={6}>
                  <Form.Item
                    name={["business_hours_global", "open"]}
                    label="Opening Time"
                  >
                    <AutoComplete
                      placeholder="Open"
                      options={openingHoursOptions}
                      allowClear
                      onSelect={(value) => {
                        const days = form.getFieldValue("business_hours") || [];
                        form.setFieldsValue({
                          business_hours: days.map((d) =>
                            d.isClosed ? d : { ...d, open: value }
                          ),
                        });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value) {
                          const days = form.getFieldValue("business_hours") || [];
                          form.setFieldsValue({
                            business_hours: days.map((d) =>
                              d.isClosed ? d : { ...d, open: value }
                            ),
                          });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    />
                  </Form.Item>
                </Col>

                <Col span={6}>
                  <Form.Item
                    name={["business_hours_global", "close"]}
                    label="Closing Time"
                  >
                    <AutoComplete
                      placeholder="Close"
                      options={closingHoursOptions}
                      allowClear
                      onSelect={(value) => {
                        const days = form.getFieldValue("business_hours") || [];
                        form.setFieldsValue({
                          business_hours: days.map((d) =>
                            d.isClosed ? d : { ...d, close: value }
                          ),
                        });
                      }}
                      onBlur={(e) => {
                        const value = e.target.value;
                        if (value) {
                          const days = form.getFieldValue("business_hours") || [];
                          form.setFieldsValue({
                            business_hours: days.map((d) =>
                              d.isClosed ? d : { ...d, close: value }
                            ),
                          });
                        }
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.List name="business_hours">
                {(fields) => (
                  <>
                    {fields.map(({ key, name }) => (
                      <Row
                        key={key}
                        gutter={12}
                        align="middle"
                        style={{ marginBottom: 8 }}
                      >
                        {/* Day */}
                        <Col span={8}>
                          <Form.Item name={[name, "day"]}>
                            <Input disabled />
                          </Form.Item>
                        </Col>

                        {/* Closed */}
                        <Col span={6}>
                          <Form.Item
                            name={[name, "isClosed"]}
                            valuePropName="checked"
                          >
                            <Checkbox
                              onChange={(e) => {
                                const checked = e.target.checked;
                                const globalOpen = form.getFieldValue([
                                  "business_hours_global",
                                  "open",
                                ]);
                                const globalClose = form.getFieldValue([
                                  "business_hours_global",
                                  "close",
                                ]);

                                if (checked) {
                                  form.setFields([
                                    {
                                      name: ["business_hours", name, "open"],
                                      value: null,
                                    },
                                    {
                                      name: ["business_hours", name, "close"],
                                      value: null,
                                    },
                                  ]);
                                } else {
                                  form.setFields([
                                    {
                                      name: ["business_hours", name, "open"],
                                      value: globalOpen ?? null,
                                    },
                                    {
                                      name: ["business_hours", name, "close"],
                                      value: globalClose ?? null,
                                    },
                                  ]);
                                }
                              }}
                            >
                              Closed
                            </Checkbox>
                          </Form.Item>
                        </Col>
                      </Row>
                    ))}
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item
              name="establishment_years"
              label="Year of Establishment"
              // rules={[
              //   { required: true, message: "Establishment year is required" },
              // ]}
            >
              <DatePicker
                picker="year"
                placeholder="Select year"
                format="YYYY"
                disableFuture
                value={date}
                minDate={dayjs(minDate)}
                maxDate={dayjs(new Date())}
                onChange={handleDateChange}
              />
            </Form.Item>
            <Form.Item
              name="team_range"
              label="Number of Team Members"
              // rules={[{ required: true, message: "Team range is required" }]}
            >
              <AutoComplete
                options={teamRangeOptions}
                placeholder="Select or type team range"
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              />
            </Form.Item>

            <Form.Item label="Business Locations / Addresses">
              <Form.List name="addresses">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row gutter={12} key={key}>
                        {/* Type */}
                        <Col span={6}>
                          <Form.Item
                            {...restField}
                            name={[name, "type"]}
                            rules={[
                              { required: true, message: "Type is required" },
                            ]}
                          >
                            <Input placeholder="Type" />
                          </Form.Item>
                        </Col>

                        {/* Address */}
                        <Col span={16}>
                          <Form.Item
                            {...restField}
                            name={[name, "address"]}
                            rules={[
                              {
                                required: true,
                                message: "Address is required",
                              },
                            ]}
                          >
                            <Input placeholder="Address" />
                          </Form.Item>
                        </Col>

                        {/* Delete icon */}
                        <Col span={2} style={{ textAlign: "center" }}>
                          <Button
                            danger
                            type="text"
                            icon={<DeleteOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    ))}

                    <Button
                      type="dashed"
                      onClick={() => add({ type: "", address: "" })}
                      style={{ width: "100%" }}
                    >
                      + Add Address
                    </Button>
                  </>
                )}
              </Form.List>
            </Form.Item>

            <Form.Item label="Minimum Project Size">
              <Row gutter={8}>
                <Col span={16}>
                  <Form.Item
                    name={["project_sizes", "sizes"]}
                    noStyle
                    // rules={[{ required: true, message: "Sizes are required" }]}
                  >
                    <Select
                      mode="tags"
                      options={projectSizeOptions.filter(
                        (option) => option.unit === selectedUnit
                      )}
                      placeholder="Select or type sizes"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["project_sizes", "unit"]}
                    noStyle
                    // rules={[{ required: true, message: "Unit is required" }]}
                  >
                    <AutoComplete
                      options={[
                        { value: "sq.ft." },
                        { value: "sq.m." },
                      ]}
                      placeholder="Unit"
                      onSelect={handleUnitChange}
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item
              name="project_location"
              label="Project/Client Location Preference"
              // rules={[
              //   { required: true, message: "Project location is required" },
              // ]}
            >
              <AutoComplete
                options={locationOptions}
                placeholder="Select or type project location"
                onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
              />
            </Form.Item>
            <Form.Item name="renovation_work" label="Renovation Work">
              <Radio.Group>
                <Radio value={true}>Yes</Radio>
                <Radio value={false}>No</Radio>
              </Radio.Group>
            </Form.Item>
            <Form.Item
              name="project_typology"
              label="Project Typologies"
              // rules={[
              //   { required: true, message: "Project typologies are required" },
              // ]}
            >
              <Select
                mode="tags"
                options={typologyOptions}
                placeholder="Select or type typologies"
              />
            </Form.Item>
            <Form.Item
              name="design_style"
              label="Product/Service Design Style"
              // rules={[
              //   { required: true, message: "Design styles are required" },
              // ]}
            >
              <Select
                mode="tags"
                options={designStyleOptions}
                placeholder="Select or type design styles"
              />
            </Form.Item>
            <Form.Item label="Approximate Budget of Projects">
              <Row gutter={8}>
                <Col span={16}>
                  <Form.Item
                    name={["avg_project_budget", "budgets"]}
                    noStyle
                    // rules={[{ required: true, message: "Budget is required" }]}
                  >
                    <Select
                      mode="tags"
                      options={averageBudgetOptions.filter(
                        (option) => option.currency === selectedCurrency
                      )}
                      placeholder="Select or type budgets"
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["avg_project_budget", "currency"]}
                    noStyle
                    // rules={[
                    //   { required: true, message: "Currency is required" },
                    // ]}
                  >
                    <Select
                      options={[
                        ...new Map(
                          averageBudgetOptions.map((item) => [
                            item.currency,
                            { label: item.currency, value: item.currency },
                          ])
                        ).values(),
                      ]}
                      placeholder="Currency"
                      onChange={handleCurrencyChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label="Current Minimal Project Fee">
              <Row gutter={8}>
                <Col span={16}>
                  <Form.Item
                    name={["project_mimimal_fee", "fee"]}
                    noStyle
                    // rules={[{ required: true, message: "Fee is required" }]}
                  >
                    <AutoComplete
                      options={minimumProjectFeeOptions}
                      placeholder="Select or type fee range"
                      onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={["project_mimimal_fee", "currency"]}
                    noStyle
                    // rules={[
                    //   { required: true, message: "Currency is required" },
                    // ]}
                  >
                    <Select
                      options={[
                        { label: "INR", value: "INR" },
                        { label: "USD", value: "USD" },
                      ]}
                      placeholder="Currency"
                      onChange={handleMinimumFeeCurrencyChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item
              name="product_positionings"
              label="Product/Service Positionings"
            >
              <Select
                mode="tags"
                options={productPositionings}
                placeholder="Select or type product/service"
              />
            </Form.Item>
          </Panel>
        </Collapse>
        <br></br>
      </div>
    </Spin>
  );
};

export default FormDetailsForm;
